import React from 'react';
import {
  Card,
  CardBody,
  VStack,
  HStack,
  Heading,
  SimpleGrid,
  Input,
  Select,
  Button,
  Badge,
  InputGroup,
  InputLeftElement,
  IconButton,
  Wrap
} from '@chakra-ui/react';
import { MdSearch, MdClose } from 'react-icons/md';

export interface FilterField {
  key: string;
  type: 'text' | 'select' | 'date';
  placeholder: string;
  options?: { value: string; label: string }[];
  icon?: React.ReactElement;
}

interface SearchFiltersProps {
  title?: string;
  filters: Record<string, string>;
  filterFields: FilterField[];
  hasActiveFilters: boolean;
  totalResults: number;
  onFilterChange: (field: string, value: string) => void;
  onClearFilters: () => void;
  children?: React.ReactNode; // Para filtros adicionales como rango de fechas
}

const SearchFilters: React.FC<SearchFiltersProps> = ({
  title = "Filtros de Búsqueda",
  filters,
  filterFields,
  hasActiveFilters,
  totalResults,
  onFilterChange,
  onClearFilters,
  children
}) => {

  const renderFilterField = (field: FilterField) => {
    const value = filters[field.key] || '';

    switch (field.type) {
      case 'select':
        return (
          <Select
            key={field.key}
            placeholder={field.placeholder}
            value={value}
            onChange={e => onFilterChange(field.key, e.target.value)}
            bg="white"
          >
            {field.options?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        );

      case 'date':
        return (
          <Input
            key={field.key}
            type="date"
            placeholder={field.placeholder}
            value={value}
            onChange={e => onFilterChange(field.key, e.target.value)}
            bg="white"
          />
        );

      case 'text':
      default:
        if (field.icon) {
          return (
            <InputGroup key={field.key}>
              <InputLeftElement pointerEvents="none">
                {field.icon}
              </InputLeftElement>
              <Input
                placeholder={field.placeholder}
                value={value}
                onChange={e => onFilterChange(field.key, e.target.value)}
                bg="white"
              />
            </InputGroup>
          );
        }
        
        return (
          <Input
            key={field.key}
            placeholder={field.placeholder}
            value={value}
            onChange={e => onFilterChange(field.key, e.target.value)}
            bg="white"
          />
        );
    }
  };

  const activeFiltersCount = Object.values(filters).filter(v => v !== '').length;

  return (
    <Card mb={4} bg="#5b182b2a">
      <CardBody>
        <VStack spacing={4}>
          <Heading size="md" color="#5b182b" alignSelf="start">
            {title}
          </Heading>
          
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} w="full">
            {filterFields.map(renderFilterField)}
          </SimpleGrid>
          
          {children && (
            <VStack w="full" spacing={4}>
              {children}
            </VStack>
          )}
          
          <HStack spacing={3} justify="space-between" w="full">
            <HStack spacing={3}>
              <Button
                color="#bb8c52"
                borderColor="#bb8c52"
                bgColor="#ffffff"
                variant="outline"
                size="sm"
                onClick={onClearFilters}
                isDisabled={!hasActiveFilters}
              >
                Limpiar Filtros
              </Button>
              
              {hasActiveFilters && (
                <Badge bg="#bb8c52" color="white" px={2} py={1} borderRadius="md">
                  {activeFiltersCount} filtro(s) activo(s)
                </Badge>
              )}
            </HStack>
            
            <Badge bg="#5b182b" color="white" variant="solid">
              Total: {totalResults}
            </Badge>
          </HStack>

          {/* Indicadores de filtros activos */}
          {hasActiveFilters && (
            <Wrap spacing={2} w="full">
              {Object.entries(filters).map(([key, value]) => {
                if (!value) return null;
                
                const field = filterFields.find(f => f.key === key);
                const label = field?.placeholder || key;
                
                return (
                  <Badge
                    key={key}
                    border="1px solid #bb8c52"
                    color="#bb8c52"
                    bg="transparent"
                    display="flex"
                    alignItems="center"
                    gap={1}
                    px={2}
                    py={1}
                    borderRadius="full"
                  >
                    {label}: "{value}"
                    <IconButton
                      aria-label={`Remover filtro ${label}`}
                      icon={<MdClose />}
                      size="xs"
                      variant="ghost"
                      minW="auto"
                      h="auto"
                      p={0}
                      color="#bb8c52"
                      onClick={() => onFilterChange(key, '')}
                    />
                  </Badge>
                );
              })}
            </Wrap>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default SearchFilters;